import { useEffect, useMemo, useState, type SyntheticEvent } from "react";
import { SailPlotApp } from "@heiselanalytics/sailplot";
import "@heiselanalytics/sailplot/styles.css";
import { resolveTenant } from "./platform/resolveTenant";
import { UnknownTenant } from "./pages/UnknownTenant";
import {
  configuratorPreviewOverrides,
  mergeConfiguratorPreview,
} from "./platform/configuratorPreview";
import { SeoMetadata } from "./components/SeoMetadata";
import { createPlatformPageExtensions } from "./extensions/platformPages";

export default function App() {
  const tenant = resolveTenant(
    window.location.hostname,
    window.location.search,
  );

  if (!tenant) return <UnknownTenant />;

  return <ResolvedTenant tenant={tenant} />;
}

function ResolvedTenant({
  tenant,
}: {
  tenant: NonNullable<ReturnType<typeof resolveTenant>>;
}) {
  const configuratorPreview =
    new URLSearchParams(window.location.search).get("configurator_preview") ===
    "1";
  const [previewOverrides, setPreviewOverrides] =
    useState<ReturnType<typeof configuratorPreviewOverrides>>(null);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const updatePath = () => setCurrentPath(window.location.pathname);
    window.addEventListener("popstate", updatePath);
    return () => window.removeEventListener("popstate", updatePath);
  }, []);

  useEffect(() => {
    if (!configuratorPreview || window.parent === window) return;
    const receiveConfiguration = (event: MessageEvent<unknown>) => {
      const overrides = configuratorPreviewOverrides(event);
      if (!overrides) return;
      setPreviewOverrides(overrides);
    };
    window.addEventListener("message", receiveConfiguration);
    window.parent.postMessage(
      { type: "sailplot-preview-ready", version: 1 },
      "*",
    );
    return () => window.removeEventListener("message", receiveConfiguration);
  }, [configuratorPreview]);

  const themeKey = previewOverrides?.theme?.mode ?? "configured";
  const config = useMemo(() => {
    const merged = mergeConfiguratorPreview(tenant.config, previewOverrides);
    if (!configuratorPreview) return merged;
    const baseNamespace = tenant.config.storageNamespace || tenant.id;
    return {
      ...merged,
      storageNamespace: `${baseNamespace}-configurator-preview-${themeKey}`,
    };
  }, [configuratorPreview, previewOverrides, tenant, themeKey]);
  const extensions = useMemo(() => {
    const previewCustomerPrivacy =
      typeof previewOverrides?.links?.privacy === "string" &&
      previewOverrides.links.privacy
        ? previewOverrides.links.privacy
        : undefined;
    const previewCustomerImprint =
      typeof previewOverrides?.links?.imprint === "string" &&
      previewOverrides.links.imprint
        ? previewOverrides.links.imprint
        : undefined;
    const base = configuratorPreview
      ? createPlatformPageExtensions({
          customerLegalLinks: {
            privacy: previewCustomerPrivacy,
            imprint: previewCustomerImprint,
          },
          hostingProvider: "cloudflare-pages",
          imprint: false,
          instructions: true,
          makeYourOwn: true,
          openSource: true,
          privacy: false,
          sailPlotLegalBaseUrl: window.location.hostname.includes("sandbox")
            ? "https://sandbox.sailplot.app/"
            : "https://sailplot.app/",
          hostedForCustomer: true,
        })
      : (tenant.extensions ?? {});
    const operatorNavigationItems = configuratorPreview
      ? [
          typeof previewOverrides?.links?.website === "string" &&
          previewOverrides.links.website
            ? {
                id: "customer-website",
                label: "Website",
                href: previewOverrides.links.website,
                external: true,
              }
            : null,
        ].filter((item) => item !== null)
      : [];
    return {
      ...base,
      ...(configuratorPreview
        ? {
            emptySelectionContent: undefined,
            compactBrandingContent: undefined,
          }
        : {}),
      navigationItems: [
        ...operatorNavigationItems,
        ...(base.navigationItems ?? []),
      ],
      onEvent: (event: Parameters<NonNullable<typeof base.onEvent>>[0]) => {
        base.onEvent?.(event);
        setCurrentPath(event.path);
      },
    };
  }, [configuratorPreview, previewOverrides, tenant.extensions]);
  const blockPreviewInteraction = (event: SyntheticEvent) => {
    if (!configuratorPreview) return;
    if (!(event.target instanceof Element)) return;
    const menuTrigger = event.target.closest(".sailplot-menu-trigger");
    const logoInteraction = event.target.closest(
      ".canvas-branding-partner, .canvas-branding-menu-popover, .mobile-branding-partner, .mobile-branding-menu-popover",
    );
    const navigationLink = event.target.closest<HTMLAnchorElement>(
      ".sailplot-menu-popover a[href]",
    );
    const sailPlotLegalBaseUrl = window.location.hostname.includes("sandbox")
      ? "https://sandbox.sailplot.app/"
      : "https://sailplot.app/";
    const clickablePreviewUrls = [
      previewOverrides?.links?.website,
      previewOverrides?.links?.privacy,
      previewOverrides?.links?.imprint,
      new URL("privacy", sailPlotLegalBaseUrl).href,
      new URL("imprint", sailPlotLegalBaseUrl).href,
    ].flatMap((value) => {
      if (typeof value !== "string" || !value) return [];
      try {
        return [new URL(value).href];
      } catch {
        return [];
      }
    });
    const configuredNavigationLink =
      navigationLink && clickablePreviewUrls.includes(navigationLink.href);
    if (menuTrigger || logoInteraction || configuredNavigationLink) {
      if (event.type === "click") {
        const link = event.target.closest<HTMLAnchorElement>("a[href]");
        if (link) {
          event.preventDefault();
          window.open(link.href, "_blank", "noopener,noreferrer");
        }
      }
      return;
    }
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <>
      <SeoMetadata
        tenantId={tenant.id}
        currentPath={currentPath}
        configuratorPreview={configuratorPreview}
      />
      <div
        className="platform-app"
        data-tenant={tenant.id}
        data-configurator-preview={configuratorPreview ? "true" : undefined}
        data-preview-properties={configuratorPreview ? "closed" : undefined}
        onClickCapture={blockPreviewInteraction}
        onContextMenuCapture={blockPreviewInteraction}
        onDoubleClickCapture={blockPreviewInteraction}
        onKeyDownCapture={blockPreviewInteraction}
        onMouseDownCapture={blockPreviewInteraction}
        onPointerDownCapture={blockPreviewInteraction}
        onTouchMoveCapture={blockPreviewInteraction}
        onTouchStartCapture={blockPreviewInteraction}
        onWheelCapture={blockPreviewInteraction}
      >
        <SailPlotApp
          key={`${tenant.id}:${themeKey}`}
          config={config}
          extensions={extensions}
        />
      </div>
    </>
  );
}
